import React from 'react'
import type { Block, Variable } from '../../types'
import { PropertyLabel, PropertyInput, ToggleSwitch } from './shared'

interface SliderPropertiesProps {
  block: Block
  variable: Variable
  linkedBlocks: Block[]
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
  onUpdateVariable: (name: string, updates: Partial<Variable>) => void
  onRenameVariable: (oldName: string, newName: string) => void
}

const ACCENT_COLORS = [
  { name: 'primary', value: '#5048e5', className: 'bg-[#5048e5]', ring: 'ring-[#5048e5]' },
  { name: 'rose', value: '#f43f5e', className: 'bg-rose-500', ring: 'ring-rose-500' },
  { name: 'emerald', value: '#10b981', className: 'bg-emerald-500', ring: 'ring-emerald-500' },
  { name: 'amber', value: '#f59e0b', className: 'bg-amber-500', ring: 'ring-amber-500' },
  { name: 'slate', value: '#0f172a', className: 'bg-slate-900', ring: 'ring-slate-900' },
]

export default function SliderProperties({
  block,
  variable,
  linkedBlocks,
  onUpdateBlock,
  onUpdateVariable,
  onRenameVariable,
}: SliderPropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>

  return (
    <>
      <div className="space-y-2">
        <PropertyLabel>Label</PropertyLabel>
        <PropertyInput
          value={block.label || ''}
          onChange={(e) => onUpdateBlock(block.id, { label: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <PropertyLabel>Linked Variable</PropertyLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[#5048e5] font-bold">@</span>
          <input
            type="text"
            value={variable.name}
            onChange={(e) =>
              onRenameVariable(variable.name, e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())
            }
            className="w-full font-mono bg-slate-50 border border-slate-200 rounded-lg p-2.5 pl-7 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <PropertyLabel>Min</PropertyLabel>
          <PropertyInput
            type="number"
            value={variable.min ?? 0}
            onChange={(e) => onUpdateVariable(variable.name, { min: Number(e.target.value) })}
          />
        </div>
        <div className="space-y-2">
          <PropertyLabel>Max</PropertyLabel>
          <PropertyInput
            type="number"
            value={variable.max ?? 100}
            onChange={(e) => onUpdateVariable(variable.name, { max: Number(e.target.value) })}
          />
        </div>
        <div className="space-y-2">
          <PropertyLabel>Default</PropertyLabel>
          <PropertyInput
            type="number"
            value={variable.default as number ?? 0}
            onChange={(e) => onUpdateVariable(variable.name, { default: Number(e.target.value) })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <PropertyLabel>Step size</PropertyLabel>
          <PropertyInput
            type="number"
            value={variable.step ?? 1}
            onChange={(e) => onUpdateVariable(variable.name, { step: Number(e.target.value) })}
          />
        </div>
        <div className="space-y-2">
          <PropertyLabel>Suffix</PropertyLabel>
          <PropertyInput
            value={(config.suffix as string) ?? '%'}
            onChange={(e) => onUpdateBlock(block.id, { config: { ...config, suffix: e.target.value } })}
            placeholder="%"
          />
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-sm font-medium text-slate-900">Link to Chart</span>
            <span className="text-[10px] text-slate-500">Auto-update connected charts</span>
          </div>
          <ToggleSwitch
            checked={(config.linkToChart as boolean) !== false}
            onChange={(v) => onUpdateBlock(block.id, { config: { ...config, linkToChart: v } })}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-sm font-medium text-slate-900">Show Value Label</span>
            <span className="text-[10px] text-slate-500">Displays real-time percentage</span>
          </div>
          <ToggleSwitch
            checked={(config.showValueLabel as boolean) !== false}
            onChange={(v) => onUpdateBlock(block.id, { config: { ...config, showValueLabel: v } })}
          />
        </div>
      </div>

      <div className="space-y-3">
        <PropertyLabel>Accent Color</PropertyLabel>
        <div className="flex flex-wrap gap-2">
          {ACCENT_COLORS.map((color) => (
            <button
              key={color.name}
              className={`size-6 rounded-full ${color.className} hover:ring-2 hover:ring-offset-2 hover:${color.ring} transition-all ${
                (config.accentColor as string) === color.value ||
                (!(config.accentColor as string) && color.name === 'primary')
                  ? 'ring-2 ring-offset-2 ' + color.ring
                  : ''
              }`}
              onClick={() => onUpdateBlock(block.id, { config: { ...config, accentColor: color.value } })}
            />
          ))}
        </div>
      </div>

      {linkedBlocks.length > 0 && (
        <div className="pt-4 border-t border-slate-100">
          <PropertyLabel>Linked Blocks</PropertyLabel>
          <ul className="space-y-1 mt-2">
            {linkedBlocks.map((lb) => (
              <li key={lb.id} className="text-sm text-slate-500">
                &bull; {lb.content?.slice(0, 30) || lb.label || lb.type}{' '}
                <span className="text-xs">({lb.type})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}
